import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { asyncScheduler } from 'rxjs';
import { map, debounceTime, withLatestFrom, filter } from 'rxjs/operators';

import { FilterResultsUnion } from './app.actions';
import { SubscriptionsParams } from '../modules/subscriptions/subscriptions.interface';
import { AnalysesParams } from '../modules/analyses/analyses.interface';
import { loadAnalyses } from '../modules/analyses/store/analyses.actions';
import { QuestionsParams } from '../modules/questions/questions.interface';
import { UsersParams } from '../modules/users/users.interface';
import { PitchesParams } from '../modules/pitches/pitches.interface';
import { loadPitches } from '../modules/pitches/store/pitches.actions';
import { CompaniesParams } from '../modules/companies/companies.interface';
import { InvestmentCompaniesParams } from '../modules/investment-companies/investment-companies.interface';
import { loadInvestments } from '../modules/investments/store/investments.actions';
import { InvestmentsParams } from '../modules/investments/investments.interface';
import { AppViews } from '../shared/helpers/enums';
import { loadSubscriptions } from '../modules/subscriptions/store/subscriptions.actions';
import { loadCompanies } from '../modules/companies/store/companies.actions';
import { loadInvestmentCompanies } from '../modules/investment-companies/store/investment-companies.actions';
import { loadInquiries } from '../modules/contact-us/store/contact-us.actions';
import { loadQuestions } from '../modules/questions/store/questions.actions';
import { loadUsers } from '../modules/users/store/users.actions';
import { SharedService } from '../core/services/shared.service';
import { SelfPublishesParams } from '../modules/self-publishes/self-publishes.interface';
import { loadOffers } from '../modules/second-hand-market/store/second-hand-market.actions';
import { SecondHandMarketParams } from '../modules/second-hand-market/second-hand-market.interface';
import { ContactUsParams } from '../modules/contact-us/contact-us.interface';
import { SelfPublishesActions } from '../modules/self-publishes/store';

@Injectable()
export class AppEffects {
  constructor(private actions$: Actions, private sharedService: SharedService, private store$: Store<any>) {}

  filterResults$ = createEffect(
    () =>
      ({ debounce = 900, scheduler = asyncScheduler } = {}) =>
        this.actions$.pipe(
          ofType('[App] Filter Results'),
          debounceTime(debounce, scheduler),
          withLatestFrom(this.store$),
          filter(([action, store]) => {
            let params: any;
            switch ((action as FilterResultsUnion).appView) {
              case AppViews.Analyses:
                params = store.analyses.params;
                break;
              case AppViews.Companies:
                params = store.companies.params;
                break;
              case AppViews.ContactUs:
                params = store.contactUs.params;
                break;
              case AppViews.InvestmentCompanies:
                params = store.investmentCompanies.params;
                break;
              case AppViews.Investments:
                params = store.investments.params;
                break;
              case AppViews.Pitches:
                params = store.pitches.params;
                break;
              case AppViews.Questions:
                params = store.questions.params;
                break;
              case AppViews.SecondHandMarket:
                params = store.secondHandMarket.params;
                break;
              case AppViews.SelfPublishes:
                params = store.selfPublishes.params;
                break;
              case AppViews.Subscriptions:
                params = store.subscriptions.params;
                break;
              case AppViews.Users:
                params = store.users.params;
                break;
            }
            return JSON.stringify((action as any).params) === JSON.stringify(params) ? null : action;
          }),
          map(([{ params, appView }]) => {
            this.sharedService.activeElement = document.activeElement;
            (document.activeElement as HTMLElement).blur();

            switch (appView) {
              case AppViews.Analyses:
                return loadAnalyses(params as AnalysesParams);
              case AppViews.Companies:
                return loadCompanies(params as CompaniesParams);
              case AppViews.ContactUs:
                return loadInquiries(params as ContactUsParams);
              case AppViews.InvestmentCompanies:
                return loadInvestmentCompanies(params as InvestmentCompaniesParams);
              case AppViews.Investments:
                return loadInvestments(params as InvestmentsParams);
              case AppViews.Pitches:
                return loadPitches(params as PitchesParams);
              case AppViews.Questions:
                return loadQuestions(params as QuestionsParams);
              case AppViews.SecondHandMarket:
                return loadOffers(params as SecondHandMarketParams);
              case AppViews.SelfPublishes:
                return SelfPublishesActions.request.loadSelfPublishes(params as SelfPublishesParams);
              case AppViews.Subscriptions:
                return loadSubscriptions(params as SubscriptionsParams);
              case AppViews.Users:
                return loadUsers(params as UsersParams);
            }
          })
        )
  );
}
